import { Centrifuge } from "centrifuge";
import { tokenStorage } from "@/shared/auth/tokenStorage";

class CentrifugeManager {
  constructor() {
    this.centrifuge = null;
    this.currentToken = null;
    this.connected = false;
    /** @type {Map<string, { ref: number, sub: object, cbs: Set<Function> }>} */
    this.subs = new Map();
  }

  init(
    token = tokenStorage.accessToken || "",
    wsUrl = window.__CONFIG__.VUE_APP_BASE_DOMAIN.replace("http", "ws") +
      "/ws/connection/websocket"
  ) {
    if (!this.centrifuge) {
      this.centrifuge = new Centrifuge(wsUrl, {
        token,
        getToken: () => Promise.resolve(tokenStorage.accessToken || ""),
      });
      this.currentToken = token;
      return;
    }

    if (this.currentToken !== token) {
      this.centrifuge.setToken(token);
      this.currentToken = token;
    }
  }

  /**
   * @param {string} channel
   * @param {(data: any, ctx: object) => void} cb
   * @returns {() => void} unsubscribe function
   */
  subscribe(channel, cb) {
    const entry = this.subs.get(channel);

    if (entry) {
      if (entry.ref === 0) {
        entry.sub.subscribe();
      }
      entry.ref += 1;
      entry.cbs.add(cb);

      if (!this.connected) {
        this.centrifuge.connect();
        this.connected = true;
      }

      return () => this._detach(channel, cb);
    }

    if (!this.centrifuge) {
      throw new Error("Centrifuge not initialised — call init() first");
    }

    const sub = this.centrifuge.newSubscription(channel);
    const cbs = new Set([cb]);

    sub.on("publication", (ctx) => {
      cbs.forEach((fn) => fn(ctx.data, ctx));
    });

    sub.subscribe();
    this.subs.set(channel, { ref: 1, sub, cbs });

    if (!this.connected) {
      this.centrifuge.connect();
      this.connected = true;
    }

    return () => this._detach(channel, cb);
  }

  /** @private */
  _detach(channel, cb) {
    const entry = this.subs.get(channel);
    if (!entry) return;

    entry.cbs.delete(cb);
    entry.ref -= 1;

    if (entry.ref === 0) {
      entry.sub.unsubscribe();
      entry.cbs.clear();
    }

    const hasActive = [...this.subs.values()].some((e) => e.ref > 0);
    if (!hasActive && this.connected) {
      this.centrifuge?.disconnect();
      this.connected = false;
    }
  }

  disconnect() {
    this.centrifuge?.disconnect();
    this.centrifuge = null;
    this.currentToken = null;
    this.subs.clear();
    this.connected = false;
  }
}

export const centrifugeManager = new CentrifugeManager();
